import { Box, InputLabel, MenuItem, Select, Skeleton } from '@mui/material';
import React, { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALE } from 'shared/constants/localization/types';

import { useLocalizationContext } from '@/context/LocalizationContext';
import { updateLocale } from '@/services/localization';

import { Props } from './types';

function LocaleSelector({ onRegionChange, regionValue, testIdPrefix }: Props) {
    const { t } = useTranslation();

    const { regions, isLoading } = useLocalizationContext();

    const changeLanguage = useCallback((language: LOCALE) => {
        updateLocale(language);
    }, []);

    const onRegionChangeEvent = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (!onRegionChange) {
                return;
            }

            const regionCode = event.target.value;
            onRegionChange(regionCode);

            const region =
                regions.find((region) => region.code === regionCode) || null;

            if (region?.proposed_language) {
                changeLanguage(region.proposed_language);
            }
        },
        [onRegionChange, regions, changeLanguage],
    );

    return (
        <Box
            sx={{
                display: 'flex',
                mb: 4,
                mr: { xs: 2, md: 0 },
                maxWidth: 512,
                flexWrap: 'wrap',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    mr: 8.5,
                    mb: 2,
                }}
            >
                <InputLabel sx={{ fontSize: 14, color: 'black' }}>
                    {t('Login:country')}
                </InputLabel>
                {isLoading ? (
                    <Skeleton
                        height={70}
                        sx={{ borderRadius: '4px', mr: 2, my: 2 }}
                        variant="text"
                        width={200}
                    />
                ) : (
                    <Select
                        sx={{ width: 200 }}
                        value={regionValue}
                        onChange={onRegionChangeEvent}
                        id="region"
                        size="small"
                        data-test-id={`${testIdPrefix}-LocaleSelector-region`}
                    >
                        {regions.map((region) => (
                            <MenuItem
                                value={region.code}
                                key={region.code}
                                data-test-id={`${testIdPrefix}-LocaleSelector-region-${region.code}`}
                            >
                                {region.name}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </Box>
        </Box>
    );
}

export default LocaleSelector;
